/* eslint-disable */
import * as React from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    Card,
} from '@mui/material';
import {
    REALM_INTERN,
    REALM_ADMIN,
    REALM_DEBUG,
    UseKeycloakCheckRole,
} from '@europrocurement/l2d-keycloak';
import { FlexyButton } from '@europrocurement/flexy-components';
import {
    chevronDownIcon,
    FaOptionIcon,
    copyIcon,
    homeIcon,
    refreshDataIcon,
    B2DSymboleIcon,
} from '@europrocurement/l2d-icons';

type ErrorDisplayProps = {
    errorMessage: string;
    errorStack?: string;
    currentPath?: string;
    userAgent?: string;
};

function ErrorDisplay({ errorMessage, errorStack, currentPath, userAgent }: ErrorDisplayProps) {
    const theme = useTheme();
    const { palette } = theme;

    const roleChecker = UseKeycloakCheckRole();
    const isIntern = roleChecker(REALM_INTERN);
    const isAdmin = roleChecker(REALM_ADMIN);
    const isDebug = roleChecker(REALM_DEBUG);

    const handleReload = (): void => {
        window.location.reload();
    };

    const handleBackHome = (): void => {
        window.location.href = '/';
    };

    const handleCopyDebugInfo = (): void => {
        const debugInfo = [
            `Error: ${errorMessage}`,
            errorStack ? `Stack:\n${errorStack}` : null,
            `URL: ${currentPath}`,
            `User Agent: ${userAgent}`,
            `Timestamp: ${new Date().toISOString()}`,
            `isIntern: ${isIntern}`,
            `isAdmin: ${isAdmin}`,
            `isDebug: ${isDebug}`,
            `App: L2D Pay`,
        ]
            .filter(Boolean)
            .join('\n\n');

        navigator.clipboard.writeText(debugInfo);
    };

    const handleClearStorage = (): void => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'start',
                p: 4,
            }}
        >
            <Box
                sx={{
                    maxWidth: 800,
                    width: '100%',
                    textAlign: 'left',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'start',
                        whitespace: 'nowrap',
                        mb: 2,
                    }}
                >
                    <B2DSymboleIcon style={{ height: 45, width: 'auto' }} />

                    <Typography
                        variant="h1"
                        component="h1"
                        color={palette.error.main}
                        sx={{
                            ml: 2,
                        }}
                    >
                        Une erreur inattendue s'est produite.
                    </Typography>
                </Box>

                <Typography
                    variant="body1"
                    color={palette.text.secondary}
                    sx={{ mb: 4 }}
                >
                    Nous vous prions de nous excuser pour la gêne occasionnée. Vous pouvez tenter de
                    recharger la page. Si le problème persiste, revenez à l'accueil.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                    <FlexyButton
                        variant="contained"
                        label="Recharger la page"
                        icon={refreshDataIcon}
                        onClick={handleReload}
                        color={palette.primary.main}
                    />

                    <FlexyButton
                        variant="outlined"
                        label="Revenir à l'accueil"
                        icon={homeIcon}
                        onClick={handleBackHome}
                        color={palette.primary.main}
                    />
                </Box>

                <Typography
                    variant="caption"
                    color={palette.text.secondary}
                    sx={{ display: 'block', mt: 4 }}
                >
                    URL: {currentPath}
                </Typography>

                {isIntern || isAdmin || isDebug ? (
                    <Card>
                        {(isIntern || isAdmin) && (
                            <Accordion
                                defaultExpanded={true}
                                sx={{
                                    textAlign: 'left',
                                    boxShadow: 'none',
                                    borderColor: undefined,
                                    backgroundColor: undefined,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<FaOptionIcon {...chevronDownIcon.props} />}
                                >
                                    <Typography
                                        variant="h4"
                                        color={palette.text.primary}
                                    >
                                        Détails techniques
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            backgroundColor: palette.background.default,
                                            p: 2,
                                            borderRadius: 1,
                                            maxHeight: 300,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {errorMessage}
                                        {'\n\n'}
                                        {errorStack}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {isDebug && (
                            <Accordion
                                defaultExpanded={true}
                                sx={{
                                    textAlign: 'left',
                                    boxShadow: 'none',
                                    borderColor: undefined,
                                    backgroundColor: undefined,
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<FaOptionIcon {...chevronDownIcon.props} />}
                                >
                                    <Typography
                                        variant="h4"
                                        color={palette.text.primary}
                                    >
                                        Outils de développement
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <FlexyButton
                                            variant="outlined"
                                            label="Copier les infos de debug"
                                            icon={copyIcon}
                                            onClick={handleCopyDebugInfo}
                                        />
                                        <FlexyButton
                                            variant="outlined"
                                            label="Vider le storage"
                                            onClick={handleClearStorage}
                                        />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Card>
                ) : null}
            </Box>
        </Box>
    );
}

export function RouteErrorBoundary() {
    const error = useRouteError();

    let errorMessage = 'Unknown error';
    let errorStack: string | undefined;

    if (isRouteErrorResponse(error)) {
        errorMessage = `${error.status} - ${error.statusText}`;
    } else if (error instanceof Error) {
        errorMessage = error.toString();
        errorStack = error.stack;
    } else if (error) {
        errorMessage = String(error);
    }

    return (
        <ErrorDisplay
            errorMessage={errorMessage}
            errorStack={errorStack}
            currentPath={window.location.href}
            userAgent={navigator.userAgent}
        />
    );
}
